import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Camera } from 'lucide-react';
import { BarcodeScanner } from '../BarcodeScanner';
import { Modal } from './LieferungComponents';

// ─── EAN-Eingabe Modal (Dual-Mode: Bluetooth-Scanner + Kamera) ───────────────
export const EanModal = ({ item, onSubmit, onSkip }) => {
  const [ean, setEan] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const inputRef = useRef();

  // Auto-Fokus: Desktop sofort, iOS-Safari nach kurzer Verzögerung
  useEffect(() => {
    const t1 = setTimeout(() => inputRef.current?.focus(), 80);
    const t2 = setTimeout(() => inputRef.current?.focus(), 400); // iOS-Fallback
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  const handleScanned = (code) => {
    setShowScanner(false);
    if (code && code.trim().length >= 8) onSubmit(code.trim());
  };

  return (
    <>
      <Modal title="EAN-Code einscannen" subtitle={item.article_name} onClose={onSkip}>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          Neues Produkt – FremdArtNr <span className="font-mono font-semibold">{item.fremd_article_number}</span>.
          Bitte EAN einscannen oder eingeben.
        </p>
        <form onSubmit={e => { e.preventDefault(); if (ean.trim().length >= 8) onSubmit(ean.trim()); }} className="space-y-3">
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            value={ean}
            onChange={e => setEan(e.target.value)}
            autoFocus
            placeholder="EAN-Code (8–14 Stellen)"
            maxLength={14}
            className="w-full px-3 py-3.5 rounded-xl border-2 border-primary/40 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary font-mono text-center text-xl tracking-widest"
            data-testid="ean-input"
          />
          <p className="text-xs text-center text-gray-400 dark:text-gray-500 hidden sm:block">
            Bluetooth-Scanner: einfach scannen – Feld ist bereit
          </p>
          {/* Buttons: stacked on mobile, side-by-side on sm+ */}
          <div className="flex flex-col sm:flex-row gap-2">
            <button type="submit" disabled={ean.trim().length < 8}
              className="w-full sm:flex-1 px-4 py-3.5 bg-primary text-white rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-40 transition-all min-h-[48px]"
              data-testid="ean-submit-btn">
              Speichern & Weiter
            </button>
            <button type="button" onClick={() => setShowScanner(true)}
              className="w-full sm:w-auto px-4 py-3.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-xl text-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition-all flex items-center justify-center gap-1.5 min-h-[48px]"
              data-testid="ean-camera-btn">
              <Camera className="h-4 w-4" /> Kamera
            </button>
            <button type="button" onClick={onSkip}
              className="w-full sm:w-auto px-4 py-3.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-xl text-sm hover:opacity-80 transition-all min-h-[48px]"
              data-testid="ean-skip-btn">
              Überspringen
            </button>
          </div>
        </form>
      </Modal>
      {showScanner && (
        <BarcodeScanner onDetected={handleScanned} onClose={() => setShowScanner(false)} />
      )}
    </>
  );
};

// ─── MHD-Prompt Modal (Ja/Nein) ──────────────────────────────────────────────
export const MhdPromptModal = ({ item, onYes, onNo, onNeverAsk }) => {
  const hasPdfMhd = !!item.mhd_from_pdf;
  return (
    <Modal
      title={hasPdfMhd ? 'MHD bestätigen' : 'MHD vorhanden?'}
      subtitle={item.article_name}
      onClose={onNo}
    >
      {hasPdfMhd ? (
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <Calendar className="h-5 w-5 text-blue-500 flex-shrink-0" />
            <div>
              <p className="text-xs text-blue-600 dark:text-blue-400">MHD aus Lieferschein</p>
              <p className="text-lg font-bold text-blue-700 dark:text-blue-300">{item.mhd_from_pdf}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => onYes(item.mhd_from_pdf)} className="flex-1 px-4 py-2.5 bg-green-500 text-white rounded-lg text-sm font-semibold hover:bg-green-600 transition-all" data-testid="mhd-confirm-btn">
              MHD bestätigen
            </button>
            <button onClick={() => onYes(null)} className="px-4 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-lg text-sm hover:opacity-80 transition-all" data-testid="mhd-change-btn">
              Ändern
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Hat dieser Artikel ein Mindesthaltbarkeitsdatum (MHD)?
          </p>
          <div className="flex gap-2">
            <button onClick={() => onYes(null)} className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg text-sm font-semibold hover:opacity-90 transition-all" data-testid="mhd-yes-btn">
              Ja, MHD eingeben
            </button>
            <button onClick={onNo} className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:opacity-80 transition-all" data-testid="mhd-no-btn">
              Nein
            </button>
          </div>
          {onNeverAsk && (
            <button
              onClick={onNeverAsk}
              className="w-full px-4 py-2 text-xs text-gray-400 dark:text-gray-500 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg border border-dashed border-gray-200 dark:border-gray-700 hover:border-amber-300 dark:hover:border-amber-700 transition-all"
              data-testid="mhd-never-ask-btn"
            >
              Nein, niemals MHD
            </button>
          )}
        </div>
      )}
    </Modal>
  );
};

// ─── MHD-Eingabe Modal ────────────────────────────────────────────────────────
export const MhdInputModal = ({ item, onSubmit, onSkip, locationId }) => {
  const [mhd, setMhd] = useState('');
  const [articleNr, setArticleNr] = useState('');
  const [articleNrLoading, setArticleNrLoading] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL;
  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  // Pre-fill: gespeicherte Art.-Nr. für diese EAN laden
  useEffect(() => {
    const ean = item?.ean;
    if (!ean) return;
    setArticleNrLoading(true);
    const params = new URLSearchParams({ ean });
    if (locationId) params.set('location_id', locationId);
    fetch(`${API}/api/article-nr/lookup?${params}`, { headers: authHeader() })
      .then(r => r.json())
      .then(d => { if (d.article_number) setArticleNr(d.article_number); })
      .catch(() => {})
      .finally(() => setArticleNrLoading(false));
  }, [item?.ean, locationId]);

  return (
    <Modal title="MHD eingeben" subtitle={item.article_name} onClose={onSkip}>
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
        Bitte das Mindesthaltbarkeitsdatum vom Produkt eintragen.
      </p>
      <div className="space-y-3">
        <input
          type="date" value={mhd} onChange={e => setMhd(e.target.value)} autoFocus
          className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          data-testid="mhd-date-input"
        />
        {/* Interne Artikelnummer (JET/Gesellschafter) – optional */}
        {item?.ean && (
          <div className="space-y-1">
            <label className="text-xs text-gray-500 dark:text-gray-400 font-medium">
              Interne Art.-Nr. (JET/optional)
              {articleNrLoading && <span className="ml-1 text-xs text-gray-400">…</span>}
            </label>
            <input
              type="text"
              value={articleNr}
              onChange={e => setArticleNr(e.target.value)}
              placeholder="z.B. 15302"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary/40"
              data-testid="mhd-article-nr-input"
            />
          </div>
        )}
        <div className="flex gap-2">
          {/* onSubmit erhält MHD + optionale Art.-Nr. (rückwärtskompatibel: nur mhd wenn kein item.ean) */}
          <button onClick={() => mhd && onSubmit(mhd, articleNr.trim() || null)} disabled={!mhd}
            className="flex-1 px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 disabled:opacity-40 transition-all"
            data-testid="mhd-submit-btn">
            Bestätigen
          </button>
          <button onClick={onSkip}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-lg text-sm hover:opacity-80 transition-all"
            data-testid="mhd-skip-btn">
            Ohne MHD
          </button>
        </div>
      </div>
    </Modal>
  );
};
